type Option = {
  displayText: string,
  value: string
}
interface SelectInputProps {
  label?: String,
  optionList: Option[],
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void,
  value?: string,
  isDisabled?: Boolean,
  name?: string,
  /** For a select with no visible label. */
  ariaLabel?: string,
  className?: string,
}

/**
 * The select that sits next to TextInput in a form row, so it takes the same
 * label, height, border, fill and focus ring. It used to be a 50px box with
 * its own label style, which pushed it out of line with any field beside it.
 */
const SelectInput = (props: SelectInputProps) => {
  const { label, optionList, onChange, isDisabled = false, value, name, ariaLabel, className = '' } = props;
  const uniqueId = `${name || 'select'}-${Math.floor(100000 + Math.random() * 900000)}`;

  return (
    <div className={`flex flex-col gap-1.5 w-full ${className}`}>
      {label && (
        <label htmlFor={uniqueId} className="text-ds-135 font-medium leading-[1.4] text-white">
          {label}
        </label>
      )}
      <div className="relative">
        <select
          id={uniqueId}
          name={name}
          aria-label={label ? undefined : ariaLabel}
          className={[
            'w-full appearance-none outline-none rounded-control border bg-navy-800 text-white',
            // Same box as TextInput: 44px touch target on mobile, 38px on desktop.
            'h-11 pl-3 pr-9 lg:h-[38px]',
            'text-[16px] lg:text-ds-135',
            'border-navy-400 transition-colors duration-[120ms]',
            'focus:border-action focus:shadow-[0_0_0_3px_rgba(32,138,236,0.25)]',
            isDisabled ? 'cursor-not-allowed opacity-60' : 'hover:border-navy-300 cursor-pointer',
          ].join(' ')}
          onChange={onChange}
          value={value}
          disabled={!!isDisabled}
        >
          {optionList.map((opt, idx) => (
            <option key={idx} value={opt.value}>{opt.displayText}</option>
          ))}
        </select>
        <svg
          aria-hidden="true"
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-navy-200"
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
        >
          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
  );
};

export default SelectInput;
